import { ArrowRight, Camera, ClipboardEdit, FileText, Mic, Ruler, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

/* ============================================================
 * §2 · UNDERSTAND — warm outdoor-neutral environment
 * ============================================================ */
export const UnderstandSection = ({ c }) => {
  const rows = [
    { icon: Mic,           title: c.understand.voice_title,  body: c.understand.voice_body,
      right: (
        <div className="flex items-end gap-0.5 h-6" aria-hidden>
          {[6,10,16,22,14,24,18,10,20,26,16,8,12,18,22,14,8].map((h,i) => (
            <span key={i} className="w-1 rounded-full bg-[var(--jc-navy)]" style={{ height: `${h}px`, opacity: 0.85 }} />
          ))}
        </div>
      )},
    { icon: Camera,        title: c.understand.photos_title, body: c.understand.photos_body,
      right: (
        <div className="flex items-center gap-1.5">
          {[0,1,2].map(i => (
            <span key={i} className="w-9 h-9 rounded-md overflow-hidden border border-white shadow-sm"
              style={{ backgroundImage: "url('/brand/hero-contractor-floor-measure-v1.png')", backgroundSize: "cover", backgroundPosition: `${20+i*25}% ${30+i*10}%` }} />
          ))}
          <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-[#EEF3FE] text-[var(--jc-text)]">+24</span>
        </div>
      )},
    { icon: FileText,      title: c.understand.docs_title,   body: c.understand.docs_body,
      right: (
        <div className="flex items-center gap-1">
          {[0,1,2,3].map(i => (
            <span key={i} className="w-6 h-8 rounded-sm bg-white border border-[var(--jc-border)] shadow-sm relative overflow-hidden">
              <span className="absolute inset-x-1 top-1.5 h-0.5 bg-[var(--jc-border)] rounded" />
              <span className="absolute inset-x-1 top-3 h-0.5 bg-[var(--jc-border)] rounded" />
              <span className="absolute inset-x-1 top-4.5 h-0.5 bg-[var(--jc-border)] rounded" />
              {i === 0 && <span className="absolute bottom-0.5 right-0.5 text-[8px] font-bold text-[var(--jc-red)] leading-none">PDF</span>}
            </span>
          ))}
        </div>
      )},
    { icon: Ruler,         title: c.understand.meas_title,   body: c.understand.meas_body,
      right: (
        <div className="flex items-center gap-2">
          <div className="text-right px-3 py-1.5 rounded-lg bg-[#EEF3FE]">
            <div className="text-[15px] font-bold text-[var(--jc-blue)] leading-tight">1,284 sq ft</div>
            <div className="text-[10px] text-[var(--jc-text-soft)] uppercase tracking-wider">{c.understand.meas_area_label}</div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#EEF3FE]">
            <ShieldCheck size={14} className="text-[var(--jc-blue)]" />
            <div className="text-left leading-tight">
              <div className="text-[11.5px] font-semibold text-[var(--jc-text)]">{c.understand.meas_badge_title}</div>
              <div className="text-[10px] font-semibold text-[var(--jc-text)]">{c.understand.meas_badge_status}</div>
              <div className="text-[9.5px] text-[var(--jc-text-soft)]">{c.understand.meas_badge_sub}</div>
            </div>
          </div>
        </div>
      )},
    { icon: ClipboardEdit, title: c.understand.obs_title,    body: c.understand.obs_body,
      right: (
        <ul className="text-[12.5px] text-[var(--jc-blue)] space-y-0.5">
          <li>• {c.understand.obs1}</li>
          <li>• {c.understand.obs2}</li>
          <li>• {c.understand.obs3}</li>
        </ul>
      )},
  ];

  return (
    <section
      data-testid="features-understand"
      className="relative overflow-hidden lg:min-h-[960px]"
    >
      {/* Full-bleed §2 environmental canvas — clean gradient
          negative space on the LEFT (live-copy zone) + Levi at
          framed-house construction site reviewing plans on the
          RIGHT. Composition is intentional; NO cream/beige/tan
          overlay, NO scrim, NO gradient wash, NO baked UI. */}
      <img
        src="/brand/features/understand_env_canvas.png"
        alt="Levi at a framed-house construction site reviewing house plans with pen in paw, Job Walk Notes, phone, tumbler, and laptop; ladder and lumber behind him."
        draggable={false}
        className="hidden lg:block absolute inset-0 w-full h-full object-cover object-center select-none"
        style={{ zIndex: 0 }}
      />

      <div
        className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16 lg:pt-20 lg:pb-24"
        style={{ zIndex: 2 }}
      >
        <div className="grid grid-cols-12 gap-8 lg:gap-8 items-start">
          {/* LEFT · headline + body + 5 capability cards + CTA */}
          <div className="col-span-12 lg:col-span-5">
            <h2 className="font-display leading-[1.02] tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-[54px]">
              {c.understand.h2a}
              <br />
              <span className="text-[var(--jc-blue)]">{c.understand.h2b}</span>
            </h2>
            <p className="mt-5 text-[17px] leading-[1.5] font-medium text-[#0A1428] max-w-lg">
              {c.understand.lede}
            </p>
            <div className="mt-8 space-y-4 max-w-[620px]">
              {rows.map((r) => (
                <div key={r.title}
                     className="rounded-2xl bg-white/95 border border-white shadow-lg p-5"
                     data-testid={`understand-row-${r.title.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="flex items-center gap-4 lg:gap-5">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                      <r.icon size={24} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{r.title}</div>
                      <div className="text-[14.5px] text-[var(--jc-text-soft)] leading-snug mt-1">{r.body}</div>
                    </div>
                    <div className="hidden lg:block flex-shrink-0">{r.right}</div>
                  </div>
                  {/* Right-side data stacks BELOW on mobile so title/body
                      never get squeezed at narrow widths. */}
                  <div className="lg:hidden mt-4 flex justify-end">{r.right}</div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/schedule-a-demo"
                data-testid="understand-cta"
                className="jc-btn-blue px-6 py-3.5 rounded-md font-semibold inline-flex items-center gap-2 shadow-lg text-[15px]"
              >
                {c.understand.cta} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* RIGHT · reserved for the environmental canvas (Levi
              + plans + construction site are baked into the
              image). No React element required to render here. */}
          <div className="hidden lg:block lg:col-span-7" aria-hidden />
        </div>
      </div>

      {/* MOBILE ONLY · Dedicated Levi visual moment. Crops to the
          RIGHT half of the desktop split canvas so the pale-blue
          negative-space area is excluded and Levi + plans + wall
          sign + office environment fill the frame. */}
      <div className="lg:hidden max-w-7xl mx-auto px-6 pb-8">
        <div className="w-full h-[440px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]">
          <img
            src="/brand/features/understand_env_canvas.png"
            alt="Levi at his contractor's office desk reviewing house plans with the BUILT BY A CONTRACTOR, FOR CONTRACTORS wall sign behind him; hard hat, plants, Job Walk Notes notebook, phone, tumbler, and laptop in the office environment."
            draggable={false}
            className="w-full h-full object-cover select-none"
            style={{ objectPosition: "78% 55%", transform: "scale(1.35)", transformOrigin: "72% 55%" }}
          />
        </div>
      </div>
    </section>
  );
};
